<!-- Tags button + popup panel for the bulk-actions toolbar.
     Owns its open/close state, hint, click-to-nudge behavior, and Escape
     handling. Coordinates with sibling panels via provideBulkActionsPanelGroup
     in the parent toolbar. -->
<template>
  <WrapWithHint class="control" :content="hintText">
    <div @click="onWrapperClick">
      <button ref="buttonRef" class="button" :class="buttonClasses" @click="toggle">
        <span class="icon">
          <i class="fa fa-tags"></i>
        </span>
        <span>Tags</span>
      </button>
    </div>
  </WrapWithHint>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { h, ref, watchEffect } from "vue";
  import { onKeyStroke } from "@vueuse/core";

  import BulkTagPanel from "./bulk-tag-panel.vue";
  import WrapWithHint from "./wrap-with-hint.vue";
  import { createOptionsPanel } from "./lib/use-bulk-actions.js";

  const props = defineProps({
    // The model ids the bulk action will apply to.
    ids: { type: Array, required: true },
    // Selection size. Drives enabled state, hint text, and button styling.
    count: { type: Number, required: true },
    // Pinia tag store. See bulk-tag-panel.vue for the contract.
    tagStore: { type: Object, required: true },
    // "recipe" | "ingredient", used in the panel title and the hint.
    entityLabel: { type: String, required: true },
    // Event dispatched on `document` when the user clicks the button with an
    // empty selection. List components listen for this to pulse their
    // checkbox column. Pass null to skip the empty-click nudge.
    hintEventName: { type: String, default: null },
  });

  const emit = defineEmits(["applied"]);

  const buttonRef = _ref(null);
  const openState = ref(false);

  const hintText = _computed(() => {
    if (props.count === 0) return `Select ${props.entityLabel}s to add/remove tags`;
    return null;
  });

  const buttonClasses = _computed(() => {
    if (openState.value) return ["is-link", "is-active"];
    if (props.count > 0) return ["is-outlined", "is-info"];
    return ["is-outlined", "click-disabled"];
  });

  watchEffect(() => {
    if (props.count === 0) {
      openState.value = false;
    }
  });

  const panel = createOptionsPanel({
    buttonRef: () => buttonRef.value,
    openState,
    isEnabled: () => props.count > 0,
    buildContent: (keyRef) => ({
      render: () =>
        h("div", { class: "box well" }, [
          h(BulkTagPanel, {
            "key": keyRef.value,
            "ids": props.ids,
            "entity-label": props.entityLabel,
            "store": props.tagStore,
            "onClose": toggle,
            "onDone": onTagsUpdated,
          }),
        ]),
    }),
  });

  function toggle() {
    panel.toggle();
  }

  function onWrapperClick() {
    if (props.count === 0 && props.hintEventName) {
      document.dispatchEvent(new CustomEvent(props.hintEventName));
    }
  }

  function onTagsUpdated() {
    panel.hide();
    emit("applied");
  }

  onKeyStroke((ev) => {
    if (ev.key !== "Escape" || !panel.isOpen()) return;
    // Don't close the panel if the user is typing in the search input;
    // they may have hit Escape to cancel typing, not to dismiss the panel.
    const active = document.activeElement;
    if (active && active.tagName === "INPUT") return;
    panel.hide();
  });
</script>
